import React, { useCallback } from "react";
import { IconButton, NumberInput } from "@bloomberg/media.atomics";
import { RemoveCircleOutline } from "@bloomberg/media.icons";

import styles from "./styles.module.css";
import Datepicker from "~components/Datepicker";

export type HoldingRowState = {
  readonly id: string;
  readonly date: string;
  readonly shares: string;
  readonly price: string;
};

type HoldingsRowProps = {
  readonly holding: HoldingRowState;
  readonly onDateChange: (id: string, value: string) => void;
  readonly onSharesChange: (id: string, value: string) => void;
  readonly onPriceChange: (id: string, value: string) => void;
  readonly onRemove: (id: string) => void;
  readonly disableRemove?: boolean;
};

export default function HoldingsRow({
  holding,
  onDateChange,
  onSharesChange,
  onPriceChange,
  onRemove,
  disableRemove = false,
}: HoldingsRowProps) {
  const { id, date, shares, price } = holding;

  const handleDateChange = useCallback(
    (value: Date) => {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");

      onDateChange(id, `${year}-${month}-${day}`);
    },
    [id, onDateChange]
  );

  const handleSharesChange = useCallback(
    (value: string) => onSharesChange(id, value),
    [id, onSharesChange]
  );

  const handlePriceChange = useCallback(
    (value: string) => onPriceChange(id, value),
    [id, onPriceChange]
  );

  const handleRemove = useCallback(() => onRemove(id), [id, onRemove]);

  return (
    <div className={styles.holdingsRow}>
      <div className={`${styles.holdingsCell} ${styles.holdingsDatepicker}`}>
        <Datepicker
          native={false}
          label=""
          dateFormat="yyyy-MM-dd"
          placeholder="YYYY-MM-DD"
          maxDate={new Date()}
          popperPlacement="top-start"
          value={date}
          onChange={handleDateChange}
        />
      </div>
      <div className={styles.holdingsCell}>
        <NumberInput
          labelText="Shares"
          size="small"
          step={1}
          value={shares}
          onChange={handleSharesChange}
        />
      </div>
      <div className={styles.holdingsCell}>
        <NumberInput
          labelText="Price"
          size="small"
          step={0.01}
          value={price}
          onChange={handlePriceChange}
        />
      </div>
      <div className={styles.holdingsRemoveBtn}>
        <IconButton
          variant="ghost"
          size="small"
          label="Remove holding"
          onClick={handleRemove}
          disabled={disableRemove}
        >
          <RemoveCircleOutline />
        </IconButton>
      </div>
    </div>
  );
}
